import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { overheadCategoryKeys } from "@/lib/query";
import type {
  AddOverheadSubcategoryInput,
  UpdateOverheadCategoryInput,
  UpdateOverheadSubcategoryInput,
} from "@/schemas/overhead-categories";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface OverheadSubcategoryItem {
  id: string;
  name: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface OverheadCategory {
  id: string;
  code: string;
  label: string;
  active: boolean;
  sortOrder: number;
  activeSubcategoryCount: number;
  totalSubcategoryCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface OverheadCategoryDetail {
  id: string;
  code: string;
  label: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  subcategories: (OverheadSubcategoryItem & { overheadCategoryId: string })[];
}

export interface OverheadImpact {
  currentWeekEntries: number;
  totalEntries: number;
}

export interface ActiveOverheadCategory {
  id: string;
  code: string;
  label: string;
  subcategories: Array<{ id: string; name: string }>;
}

// ── Queries ────────────────────────────────────────────────────────────────────

export interface ListOverheadCategoriesParams {
  search?: string;
  includeInactive?: boolean;
}

export function useOverheadCategories(params: ListOverheadCategoriesParams = {}) {
  return useQuery({
    queryKey: overheadCategoryKeys.list(params),
    queryFn: async (): Promise<OverheadCategory[]> => {
      const url = new URLSearchParams();
      if (params.search) url.set("search", params.search);
      if (params.includeInactive) url.set("includeInactive", "true");
      const res = await fetch(`/api/overhead-categories?${url}`);
      if (!res.ok) throw new Error("Failed to fetch overhead categories");
      const json = await res.json();
      return (json as { data: OverheadCategory[] }).data;
    },
  });
}

export function useOverheadCategory(id: string, enabled = true) {
  return useQuery({
    queryKey: overheadCategoryKeys.detail(id),
    queryFn: async (): Promise<OverheadCategoryDetail> => {
      const res = await fetch(`/api/overhead-categories/${id}`);
      if (!res.ok) throw new Error("Failed to fetch overhead category");
      const json = await res.json();
      return (json as { data: OverheadCategoryDetail }).data;
    },
    enabled: enabled && !!id,
  });
}

export function useOverheadCategoryImpact(id: string, enabled = false) {
  return useQuery({
    queryKey: ["overhead-category-impact", "category", id],
    queryFn: async (): Promise<OverheadImpact> => {
      const res = await fetch(`/api/overhead-categories/${id}/impact`);
      if (!res.ok) throw new Error("Failed to fetch impact");
      const json = await res.json();
      return (json as { data: OverheadImpact }).data;
    },
    enabled: enabled && !!id,
  });
}

export function useOverheadSubcategoryImpact(subId: string, enabled = false) {
  return useQuery({
    queryKey: ["overhead-category-impact", "subcategory", subId],
    queryFn: async (): Promise<OverheadImpact> => {
      const res = await fetch(`/api/overhead-categories/subcategories/${subId}/impact`);
      if (!res.ok) throw new Error("Failed to fetch impact");
      const json = await res.json();
      return (json as { data: OverheadImpact }).data;
    },
    enabled: enabled && !!subId,
  });
}

export function useActiveOverheadCategories() {
  return useQuery({
    queryKey: overheadCategoryKeys.active(),
    queryFn: async (): Promise<ActiveOverheadCategory[]> => {
      const res = await fetch("/api/overhead-categories/active");
      if (!res.ok) throw new Error("Failed to fetch overhead categories");
      const json = await res.json();
      return (json as { data: ActiveOverheadCategory[] }).data;
    },
  });
}

// ── Category mutations ─────────────────────────────────────────────────────────

export function useUpdateOverheadCategoryLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateOverheadCategoryInput) => {
      const res = await fetch(`/api/overhead-categories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to update label");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Label updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeactivateOverheadCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/overhead-categories/${id}/deactivate`, { method: "PATCH" });
      if (res.status === 409) {
        const data = await res.json();
        const count = (data as { count?: number }).count ?? 0;
        throw new Error(
          `Cannot deactivate — ${count} open entr${count === 1 ? "y" : "ies"} in the current week`,
        );
      }
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to deactivate");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Category deactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useReactivateOverheadCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/overhead-categories/${id}/reactivate`, { method: "PATCH" });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to reactivate");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Category reactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// ── Subcategory mutations ──────────────────────────────────────────────────────

export function useAddOverheadSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      categoryId,
      ...data
    }: { categoryId: string } & AddOverheadSubcategoryInput) => {
      const res = await fetch(`/api/overhead-categories/${categoryId}/subcategories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to add subcategory");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Subcategory added");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateOverheadSubcategoryName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & UpdateOverheadSubcategoryInput) => {
      const res = await fetch(`/api/overhead-categories/subcategories/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to update subcategory");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Subcategory updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeactivateOverheadSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/overhead-categories/subcategories/${id}/deactivate`, {
        method: "PATCH",
      });
      if (res.status === 409) {
        const data = await res.json();
        const count = (data as { count?: number }).count ?? 0;
        throw new Error(
          `Cannot deactivate — ${count} open entr${count === 1 ? "y" : "ies"} in the current week`,
        );
      }
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to deactivate");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Subcategory deactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useReactivateOverheadSubcategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/overhead-categories/subcategories/${id}/reactivate`, {
        method: "PATCH",
      });
      if (!res.ok) {
        const json = await res.json();
        const errMsg = (json as { error?: string }).error;
        throw new Error(
          errMsg === "parent_inactive"
            ? "Cannot reactivate — parent category is inactive"
            : (errMsg ?? "Failed to reactivate"),
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overheadCategoryKeys.all });
      toast.success("Subcategory reactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
